import { defineStore } from 'pinia'
import { ref } from 'vue'
import axiosInstance from '@/helpers/axios'
import { SwalSuccess, SwalError, SwalDelete } from '@/helpers/mySwal'
import { useJobStore } from './job'

export const useCustomerStore = defineStore('customer', () => {
  const jobStore = useJobStore()
  const customers = ref([])
  const customer = ref({})
  const loading = ref(false)
  const isLoaded = ref(false)
  const isLoadedOne = ref(false)

  const getAllCustomers = async () => {
    if (loading.value) {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      await getAllCustomers()
    } else if (!isLoaded.value) {
      await readAllCustomers()
    }
  }

  const readAllCustomers = async () => {
    loading.value = true
    try {
      const { data } = await axiosInstance.get('/api/customers')
      customers.value = data
      isLoaded.value = true
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'טעינת לקוחות נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const getOneCustomer = async (id) => {
    loading.value = true
    try {
      const { data } = await axiosInstance.get(`/api/customers/${id}`)
      customer.value = data
      isLoadedOne.value = true
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'טעינת לקוח נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const createCustomer = async (data) => {
    loading.value = true
    try {
      const { data: customer } = await axiosInstance.post('/api/customers', data)
      if (isLoaded.value) {
        customers.value.push(customer)
      }

      SwalSuccess.fire({
        title: 'לקוח חדש נוצר בהצלחה'
      })

      return customer.id
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'הוספת לקוח נכשלה',
        confirmButtonText: 'אישור'
      })
    } finally {
      loading.value = false
    }
  }

  const updateCustomer = async (data) => {
    loading.value = true
    try {
      const { data: updatedCustomer } = await axiosInstance.put(`/api/customers/${data.id}`, data)

      if (isLoaded.value) {
        customers.value = customers.value.map((customer) =>
          customer.id === data.id ? updatedCustomer : customer
        )
      }

      if (isLoadedOne.value) {
        customer.value = updatedCustomer
      }

      jobStore.isLoaded = false

      SwalSuccess.fire({
        title: 'פרטי הלקוח עודכנו בהצלחה'
      })

      return updatedCustomer.id
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'עדכון לקוח נכשל'
      })
    } finally {
      loading.value = false
    }
  }

  const deleteCustomer = async (id) => {
    loading.value = true
    try {
      await axiosInstance.delete(`/api/customers/${id}`)
      if (isLoaded.value) {
        customers.value = customers.value.filter((customer) => customer.id !== id)
      }
      jobStore.isLoaded = false

      SwalSuccess.fire({
        title: 'פרטי הלקוח נמחקו בהצלחה'
      })

      return true
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'מחיקת לקוח נכשלה'
      })
      return false
    } finally {
      loading.value = false
    }
  }

  const confirmDelete = async (id) => {
    const confirm = await SwalDelete.fire({
      text: `האם אתה בטוח שברצונך למחוק את הלקוח?
      במחיקת לקוח ימחקו כל העבודות והתשלומים המשויכים אליו!`
    })

    if (confirm.isConfirmed) {
      const result = await deleteCustomer(id)

      return result
    }
  }

  const getCustomerName = (id) => {
    const customer = customers.value.find((customer) => customer.id === id)
    return customer?.name || ''
  }

  const $reset = () => {
    customers.value = []
    customer.value = {}
    loading.value = false
    isLoaded.value = false
    isLoadedOne.value = false
  }

  return {
    customers,
    customer,
    loading,
    isLoaded,
    isLoadedOne,
    getAllCustomers,
    getOneCustomer,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    confirmDelete,
    getCustomerName,
    $reset
  }
})
